import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Archive, ChevronDown, MessageSquareText, Plus, Save, Send, Star } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import {
  DEFAULT_INTERVALS_DAYS,
  DEFAULT_MAX_ATTEMPTS,
  SCRIPT_VARIABLES,
  renderScript,
} from "@shared/prospecting";

function parseIntervals(text: string): number[] {
  return text
    .split(/[,\s]+/)
    .map((t) => Number(t))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 60);
}

/**
 * Configuração da fila de prospecção: cria o funil (uma vez), ajusta a
 * cadência e mantém os roteiros do "Abordar no WhatsApp".
 */
export function ProspectingSection() {
  const utils = trpc.useUtils();
  const pipelinesQuery = trpc.prospecting.pipelines.list.useQuery();
  const allPipelines = trpc.crm.pipelines.list.useQuery({});
  const pipelines = pipelinesQuery.data ?? [];
  const current = pipelines[0] ?? null;

  const invalidate = () => {
    utils.prospecting.pipelines.list.invalidate();
    utils.crm.pipelines.list.invalidate();
  };

  // ── Setup ──
  const [salesPipelineId, setSalesPipelineId] = useState<string>("");
  const [salesStageId, setSalesStageId] = useState<string>("");
  const stagesQuery = trpc.crm.stages.list.useQuery(
    { pipelineId: Number(salesPipelineId) },
    { enabled: !!salesPipelineId },
  );
  const setupMut = trpc.prospecting.pipelines.setup.useMutation({
    onSuccess: () => { toast.success("Fila de prospecção criada"); invalidate(); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  // ── Cadência ──
  const [cadenceOpen, setCadenceOpen] = useState(false);
  const [intervalsText, setIntervalsText] = useState(DEFAULT_INTERVALS_DAYS.join(", "));
  const [maxAttempts, setMaxAttempts] = useState(String(DEFAULT_MAX_ATTEMPTS));
  useEffect(() => {
    if (!current) return;
    setIntervalsText(current.config.intervalsDays.join(", "));
    setMaxAttempts(String(current.config.maxAttempts));
    setSalesPipelineId(current.config.salesPipelineId ? String(current.config.salesPipelineId) : "");
    setSalesStageId(current.config.salesStageId ? String(current.config.salesStageId) : "");
  }, [current?.pipeline.id]);
  const updateMut = trpc.prospecting.pipelines.updateConfig.useMutation({
    onSuccess: () => { toast.success("Cadência salva"); invalidate(); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const salesPipelines = (allPipelines.data ?? []).filter((p) => !p.prospectingConfig);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Send className="h-4 w-4" /> Fila de prospecção
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          O SDR aborda pelo WhatsApp com um clique e o CRM registra cada tentativa. Quem agenda
          reunião vira oportunidade no funil de vendas.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {!current ? (
          <div className="rounded-lg border-2 border-dashed p-4 space-y-3">
            <p className="text-sm">
              Ainda não há fila. Ao criar, o funil <strong>Prospecção</strong> nasce com as etapas
              Na fila → Abordado → Respondeu → Reunião agendada → Perdido.
            </p>
            <SalesTargetFields
              salesPipelines={salesPipelines}
              stages={stagesQuery.data ?? []}
              salesPipelineId={salesPipelineId}
              salesStageId={salesStageId}
              onPipeline={(v) => { setSalesPipelineId(v); setSalesStageId(""); }}
              onStage={setSalesStageId}
            />
            <Button
              onClick={() =>
                setupMut.mutate({
                  name: "Prospecção",
                  intervalsDays: DEFAULT_INTERVALS_DAYS,
                  maxAttempts: DEFAULT_MAX_ATTEMPTS,
                  salesPipelineId: salesPipelineId ? Number(salesPipelineId) : null,
                  salesStageId: salesStageId ? Number(salesStageId) : null,
                })
              }
              disabled={setupMut.isPending}
            >
              <Plus className="h-4 w-4 mr-1" /> Criar fila de prospecção
            </Button>
          </div>
        ) : (
          <Collapsible open={cadenceOpen} onOpenChange={setCadenceOpen} className="space-y-3">
            <CollapsibleTrigger asChild>
              <button type="button" className="w-full flex items-center gap-2 text-left">
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${cadenceOpen ? "" : "-rotate-90"}`} />
                <span className="text-sm font-medium">Cadência</span>
                <Badge variant="secondary">{current.pipeline.name}</Badge>
                <span className="text-xs text-muted-foreground ml-auto">
                  {current.config.maxAttempts} tentativa{current.config.maxAttempts === 1 ? "" : "s"} · intervalos {current.config.intervalsDays.join(", ") || "—"} dias
                </span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pl-6">
            <p className="text-xs text-muted-foreground">
              As etapas ficam em "Funis" acima. Não renomeie as fixas — a fila depende delas.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Máximo de tentativas</Label>
                <Input type="number" min={1} max={10} value={maxAttempts} onChange={(e) => setMaxAttempts(e.target.value)} className="h-9 w-32" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Dias entre tentativas</Label>
                <Input value={intervalsText} onChange={(e) => setIntervalsText(e.target.value)} placeholder="2, 3" className="h-9 w-48" />
                <p className="text-[11px] text-muted-foreground">
                  "2, 3" = 2ª tentativa dois dias depois da 1ª; 3ª três dias depois da 2ª.
                </p>
              </div>
            </div>
            <SalesTargetFields
              salesPipelines={salesPipelines}
              stages={stagesQuery.data ?? []}
              salesPipelineId={salesPipelineId}
              salesStageId={salesStageId}
              onPipeline={(v) => { setSalesPipelineId(v); setSalesStageId(""); }}
              onStage={setSalesStageId}
            />
            <Button
              size="sm"
              onClick={() => {
                const intervals = parseIntervals(intervalsText);
                const max = Number(maxAttempts);
                if (!Number.isInteger(max) || max < 1) return toast.error("Máximo de tentativas inválido");
                updateMut.mutate({
                  pipelineId: current.pipeline.id,
                  intervalsDays: intervals,
                  maxAttempts: max,
                  salesPipelineId: salesPipelineId ? Number(salesPipelineId) : null,
                  salesStageId: salesStageId ? Number(salesStageId) : null,
                });
              }}
              disabled={updateMut.isPending}
            >
              <Save className="h-4 w-4 mr-1" /> Salvar cadência
            </Button>
            </CollapsibleContent>
          </Collapsible>
        )}

        <ScriptsEditor />
      </CardContent>
    </Card>
  );
}

function SalesTargetFields({
  salesPipelines,
  stages,
  salesPipelineId,
  salesStageId,
  onPipeline,
  onStage,
}: {
  salesPipelines: Array<{ id: number; name: string }>;
  stages: Array<{ id: number; name: string; isWon: boolean; isLost: boolean }>;
  salesPipelineId: string;
  salesStageId: string;
  onPipeline: (v: string) => void;
  onStage: (v: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs">Ao agendar reunião, criar oportunidade no funil</Label>
        <Select value={salesPipelineId || "none"} onValueChange={(v) => onPipeline(v === "none" ? "" : v)}>
          <SelectTrigger className="h-9"><SelectValue placeholder="Escolha o funil de vendas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Não criar oportunidade de venda</SelectItem>
            {salesPipelines.map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Na etapa</Label>
        <Select value={salesStageId || "none"} onValueChange={(v) => onStage(v === "none" ? "" : v)} disabled={!salesPipelineId}>
          <SelectTrigger className="h-9"><SelectValue placeholder="Etapa inicial" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Escolha a etapa</SelectItem>
            {stages.filter((s) => !s.isWon && !s.isLost).map((s) => (
              <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function ScriptsEditor() {
  const utils = trpc.useUtils();
  const scriptsQuery = trpc.prospecting.scripts.list.useQuery();
  const scripts = scriptsQuery.data ?? [];
  const [editing, setEditing] = useState<{ id: number | null; name: string; body: string; isDefault: boolean; attemptNo: number | null } | null>(null);

  const invalidate = () => utils.prospecting.scripts.list.invalidate();
  const createMut = trpc.prospecting.scripts.create.useMutation({
    onSuccess: () => { toast.success("Roteiro criado"); invalidate(); setEditing(null); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const updateMut = trpc.prospecting.scripts.update.useMutation({
    onSuccess: () => { toast.success("Roteiro salvo"); invalidate(); setEditing(null); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const archiveMut = trpc.prospecting.scripts.archive.useMutation({
    onSuccess: () => { toast.success("Roteiro arquivado"); invalidate(); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const preview = editing
    ? renderScript(editing.body, { nome: "Maria Clara", empresa: "Prefeitura de Sinop", vendedor: "João" })
    : "";

  const [open, setOpen] = useState(false);
  const novoRoteiro = () => {
    setOpen(true);
    setEditing({ id: null, name: "", body: "Oi {{nome}}, tudo bem? Aqui é o {{vendedor}}. ", isDefault: scripts.length === 0, attemptNo: 1 });
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="space-y-3 border-t pt-4">
      <div className="flex items-center gap-2">
        <CollapsibleTrigger asChild>
          <button type="button" className="flex items-center gap-2 text-left flex-1 min-w-0">
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
            <MessageSquareText className="h-4 w-4" />
            <span className="text-sm font-medium">Roteiros</span>
            <Badge variant="outline" className="h-5 text-[11px]">{scripts.length}</Badge>
          </button>
        </CollapsibleTrigger>
        <Button size="sm" variant="outline" onClick={novoRoteiro}>
          <Plus className="h-4 w-4 mr-1" /> Novo roteiro
        </Button>
      </div>
      <CollapsibleContent className="space-y-3 pl-6">
      <p className="text-xs text-muted-foreground">
        O texto que abre pronto no WhatsApp do SDR. Cadastre vários para a mesma tentativa: na fila o
        SDR escolhe qual usar em cada lead, e o relatório mostra qual respondeu mais. Variáveis:{" "}
        {SCRIPT_VARIABLES.map((v) => (
          <code key={v.key} className="mx-0.5 rounded bg-muted px-1">{`{{${v.key}}}`}</code>
        ))}
      </p>

      {editing && (
        <div className="rounded-lg border p-3 space-y-2 bg-muted/20">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Nome</Label>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Ex.: Primeira abordagem — versão curta" className="h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tentativa</Label>
              <Select value={editing.attemptNo == null ? "any" : String(editing.attemptNo)} onValueChange={(v) => setEditing({ ...editing, attemptNo: v === "any" ? null : Number(v) })}>
                <SelectTrigger className="h-9 w-[150px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}ª tentativa</SelectItem>
                  ))}
                  <SelectItem value="any">Qualquer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-xs pb-2">
              <input type="checkbox" checked={editing.isDefault} onChange={(e) => setEditing({ ...editing, isDefault: e.target.checked })} />
              Roteiro padrão
            </label>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Texto</Label>
            <Textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={4} />
          </div>
          {preview && (
            <p className="text-xs text-muted-foreground italic">Prévia: "{preview}"</p>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={!editing.name.trim() || !editing.body.trim() || createMut.isPending || updateMut.isPending}
              onClick={() => {
                const data = { name: editing.name.trim(), body: editing.body.trim(), isDefault: editing.isDefault, attemptNo: editing.attemptNo };
                if (editing.id == null) createMut.mutate(data);
                else updateMut.mutate({ id: editing.id, ...data });
              }}
            >
              <Save className="h-4 w-4 mr-1" /> Salvar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
          </div>
        </div>
      )}

      {scripts.length === 0 ? (
        <div className="text-center py-5 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
          Nenhum roteiro. Sem roteiro, o botão só abre a conversa em branco.
        </div>
      ) : (
        <div className="space-y-1.5">
          {scripts.map((s) => (
            <div key={s.id} className="group flex items-start gap-2 border rounded-md px-3 py-2 hover:bg-muted/30">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{s.name}</span>
                  <Badge variant="outline" className="h-5 text-[10px]">{s.attemptNo ? `${s.attemptNo}ª tentativa` : "qualquer"}</Badge>
                  {s.isDefault && <Badge variant="secondary" className="h-5 text-[10px]"><Star className="h-3 w-3 mr-1" />padrão</Badge>}
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">{s.body}</p>
              </div>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setOpen(true); setEditing({ id: s.id, name: s.name, body: s.body, isDefault: s.isDefault, attemptNo: s.attemptNo ?? null }); }}>Editar</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => archiveMut.mutate({ id: s.id })}>
                <Archive className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
      </CollapsibleContent>
    </Collapsible>
  );
}
